import { Component, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Editor, detectChange } from "@hydra-editor/scenario-designer";
import "reactflow/dist/style.css";
import "@hydra-editor/scenario-designer/style";
import useScenarioConfig from "./useScenarioConfig";
import ApiTrails from "../../../../api/trail";
import ApiArticles from "../../../../api/articles";
import { articlePreviewFromPage } from "../utils/article-preview";
import { selectUser } from '../../../../store/slices/user';
import ScenarioChatbot from './scenario-chatbot';
import ScenarioAiButton from './ScenarioAiButton';
import ContentSearchModal from '../../../../components/content-search/content-search-modal';
import { CONTENT_TYPES } from '../../../../components/content-search/content-types';
const { comparePayload, onChange } = detectChange;

// Default empty scenario structure (matching flow-designer's default)
const DEFAULT_EMPTY_SCENARIO = {
  "3dcf1b68-15e2-4d25-b5f0-f08daba97fa7": {
    "branchId": "1",
    "id": "3dcf1b68-15e2-4d25-b5f0-f08daba97fa7",
    "type": "StartNode",
    "data": {
      "label": "Play",
      "deserialized": true
    },
    "nextNodes": [
      "b472b457-9ce0-41ba-a7e7-2b3cacb77505"
    ],
    "prevNodes": []
  },
  "b472b457-9ce0-41ba-a7e7-2b3cacb77505": {
    "branchId": "1",
    "id": "b472b457-9ce0-41ba-a7e7-2b3cacb77505",
    "parentNodeId": "3dcf1b68-15e2-4d25-b5f0-f08daba97fa7",
    "type": "FinishNode",
    "data": {
      "label": "Finish",
      "deserialized": true
    },
    "nextNodes": [],
    "prevNodes": [
      "3dcf1b68-15e2-4d25-b5f0-f08daba97fa7"
    ]
  }
};

class ScenarioEditorErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Scenario editor failed to render', error, errorInfo);
    // Prevent error from bubbling to parent error boundaries
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="scenario-editor-error">
          Unable to render the scenario editor. Please try reloading the page or contact support if the issue persists.
        </div>
      );
    }

    return this.props.children;
  }
}

const formatInteractionChangeMessage = (change, scenarioTitle) => {
  if (change.kind === 'added') {
    return `${change.message} to "${scenarioTitle}"`;
  } else {
    return `${change.message} in "${scenarioTitle}"`;
  }
}

// Node deletion guards (data.deletionPredicate) are injected client-side by
// augmentEnrollmentDefinitions — they are a UI concern, not meaningful content.
// Drop change-log entries that ONLY touch them so adding/editing a node doesn't
// spam the "View changes" log with one "Updated data.deletionPredicate" row per
// node.
const IGNORED_TRAIL_FIELDS = ['data.deletionPredicate'];
const isIgnoredOnlyChange = (change) =>
  change &&
  change.kind === 'edited' &&
  Array.isArray(change.fields) &&
  change.fields.length > 0 &&
  change.fields.every((field) => IGNORED_TRAIL_FIELDS.includes(field));

// LF360-2023: names the article ids a Suggestions node stores that the editor
// cannot offer (deleted, switched off, another project...).
const fetchArticleReferences = async (ids) => {
  const response = await ApiArticles.referenceStates(ids);
  return { data: response.data.references };
};

// LF360-2015: what the Suggestions picker shows when an article is previewed.
const fetchArticlePreview = async (id) => {
  const response = await ApiArticles.getById(id);
  return { data: articlePreviewFromPage(response.data.page) };
};

const ScenarioEditorWrapper = ({
  id,
  scenarioId,
  scenario,
  scenarioTitle,
  // showSimulator,
  update,
  tasks = [],
  fetchers: {
    getAlternativeMessages,
    getAllMessages,
    getOptionsByMessage,
    getAllFitFacts,
    getAllArticles,
    getFilteredFitFacts,
    getFilteredArticles,
    getAllScenario,
    getTaskManagements,
    getProfileManagement,
    getOnboardingQuestions,
    fetchImageUrlById,
    fetchImageContentById,
    uploadImage,
  },
  isPreview = false,
  itemType = 'scenario',
  itemsConfigOverride,
  isOnboarding = false,
}) => {
  const {nodesConfig, nodesDefinitions} = useScenarioConfig(tasks, itemType, itemsConfigOverride);
  const user = useSelector(selectUser);
  // Use default empty scenario if scenario prop is null/undefined
  const [updatedScenario, setUpdatedScenario] = useState(scenario || DEFAULT_EMPTY_SCENARIO);
  const [interactionChanges, setInteractionChanges] = useState([]);
  const changeBlockerRef = useRef(false);
  // Mirrors the blocker's 2s window after a version opens, for `data-ready` only
  // (LF360-1981): an edit other than adding, moving or deleting a node is dropped
  // while it is open.
  const [changesAccepted, setChangesAccepted] = useState(false);
  const [forceRenderKey, setForceRenderKey] = useState(0);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [pendingScenarioUpdate, setPendingScenarioUpdate] = useState(null);
  const [editorError, setEditorError] = useState(null);
  const trailSaveTimerRef = useRef(null);
  const onlyActiveTasks = tasks.filter(item => item.status === 'active');

  // LF360-2030: the Suggestions and FitFacts nodes add individually selected
  // items through the content search. The editor asks with what the node holds
  // already and waits; the answer is what was added, or null when closed.
  const [picker, setPicker] = useState(null);
  const openPicker = useCallback(
    (typeKey) => (selectedIds) =>
      new Promise((resolve) => setPicker({ typeKey, attachedIds: selectedIds || [], resolve })),
    []
  );
  const pickArticles = useMemo(() => openPicker('articles'), [openPicker]);
  const pickFitFacts = useMemo(() => openPicker('fitFacts'), [openPicker]);
  const closePicker = (items) => {
    picker?.resolve(items);
    setPicker(null);
  };

  const editorFetchers = useMemo(() => ({
    fetchAlternatives: getAlternativeMessages,
    fetchAllMessages: getAllMessages,
    fetchOptionsByMessage: getOptionsByMessage,
    fetchFitFacts: getAllFitFacts,
    fetchArticles: getAllArticles,
    fetchFilteredFitFacts: getFilteredFitFacts,
    fetchFilteredArticles: getFilteredArticles,
    fetchArticleReferences,
    fetchArticlePreview,
    pickArticles,
    pickFitFacts,
    fetchAllScenario: getAllScenario,
    fetchTaskManagements: getTaskManagements,
    fetchProfileManagement: getProfileManagement,
    fetchOnboardingQuestions: getOnboardingQuestions,
    uploadImage,
    fetchImageUrlById,
    fetchImageContentById,
  }), [
    getAlternativeMessages,
    getAllMessages,
    getOptionsByMessage,
    getAllFitFacts,
    getAllArticles,
    getFilteredFitFacts,
    getFilteredArticles,
    getAllScenario,
    getTaskManagements,
    getProfileManagement,
    getOnboardingQuestions,
    uploadImage,
    fetchImageUrlById,
    fetchImageContentById,
    pickArticles,
    pickFitFacts,
  ]);
  const chatbotFetchers = useMemo(() => ({
    getAllFitFacts,
    getAllArticles,
  }), [getAllFitFacts, getAllArticles]);

  // Catch async/useEffect errors that error boundaries can't catch
  useEffect(() => {
    const handleError = (event) => {
      // Check if error is from scenario-designer
      if (event.error && event.error.stack && event.error.stack.includes('scenario-designer')) {
        event.preventDefault(); // Prevent default browser error handling
        event.stopPropagation(); // Stop event from bubbling
        event.stopImmediatePropagation(); // Stop other listeners
        console.error('Scenario editor failed to render', event.error);
        setEditorError(event.error);

        // Hide React's error overlay in development mode
        setTimeout(() => {
          const errorOverlay = document.querySelector('iframe[data-error-overlay]')
            || document.querySelector('iframe[style*="position: fixed"]')
            || document.querySelector('iframe[style*="z-index: 9999"]');
          if (errorOverlay) {
            errorOverlay.style.display = 'none';
          }
          // Also try to remove the error overlay div
          const overlayDiv = document.querySelector('[data-react-error-overlay]');
          if (overlayDiv) {
            overlayDiv.style.display = 'none';
          }
        }, 100);

        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Also catch promise rejections from scenario-designer
      if (event.reason && event.reason.stack && event.reason.stack.includes('scenario-designer')) {
        event.preventDefault();
        event.stopPropagation();
        console.error('Scenario editor promise rejection', event.reason);
        setEditorError(event.reason);
        return false;
      }
    };

    // Add listeners with capture phase to catch before React
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);
  // Force re-render key when id changes
  const editorKey = `${id}-${forceRenderKey}-${updatedScenario.length}`;

  // Update scenario when prop changes (e.g., parent component loads new scenario)
  // Compare by value to avoid spurious resets from new object references
  // created by safeParse on every parent re-render
  useEffect(() => {
    const incoming = scenario || DEFAULT_EMPTY_SCENARIO;
    const changes = comparePayload(incoming, updatedScenario);
    if (changes.length > 0) {
      setUpdatedScenario(incoming);
    }
  }, [scenario]);

  // Handle pending scenario updates from chatbot
  useEffect(() => {
    if (pendingScenarioUpdate) {
      const { modifiedScenario, validation } = pendingScenarioUpdate;

      console.log('Processing pending update, updatedScenario matches:',
        JSON.stringify(updatedScenario) === JSON.stringify(modifiedScenario));

      // Add delay to ensure React has fully committed the state change
      setTimeout(() => {
        console.log('Forcing remount with key increment');
        // Now that updatedScenario state has been updated, force remount
        setForceRenderKey(prev => {
          console.log('forceRenderKey changing from', prev, 'to', prev + 1);
          return prev + 1;
        });

        // Call parent update
        update?.(modifiedScenario, validation);
      }, 100);

      // Clear pending update
      setPendingScenarioUpdate(null);
    }
  }, [updatedScenario, pendingScenarioUpdate]);

  // Force re-render when id changes
  useEffect(() => {
    onChange(updatedScenario);
    changeBlockerRef.current = true;
    setChangesAccepted(false);
    setEditorError(null); // Reset error state when scenario changes
    setForceRenderKey(prev => prev + 1);
    setTimeout(() => {
      changeBlockerRef.current = false;
      setChangesAccepted(true);
    }, 2000);
  }, [id]);

  useEffect(() => {
    if (id) {
      setInteractionChanges(onChange(updatedScenario).filter((change) => !isIgnoredOnlyChange(change)));
    }
  }, [updatedScenario, id]);

  useEffect(() => {
    if (trailSaveTimerRef.current) {
      clearTimeout(trailSaveTimerRef.current);
      trailSaveTimerRef.current = null;
    }

    if (interactionChanges.length > 0 && !isPreview) {
      const targetEntityType = itemType === 'enrollment' ? 'EnrollmentScenario' : isOnboarding ? 'OnboardingSurvey' : itemType === 'surveyTask' ? 'SurveyTask' : 'Scenario';
      const targetEntityId = scenarioId;
      const targetVersionId = id;
      const formattedInteractionChanges = interactionChanges.map((change) => ({
        targetEntityType,
        targetEntityId,
        targetVersionId,
        valueFrom: change.previousValue,
        valueTo: change.newValue,
        action: formatInteractionChangeMessage(change, scenarioTitle),
      }));

      trailSaveTimerRef.current = setTimeout(() => {
        formattedInteractionChanges.forEach((change) => {
          ApiTrails.createTrail(change).catch((error) => {
            console.error('Failed to create scenario trail', error);
          });
        });
      }, 1200);
    }

    return () => {
      if (trailSaveTimerRef.current) {
        clearTimeout(trailSaveTimerRef.current);
        trailSaveTimerRef.current = null;
      }
    };
  }, [interactionChanges, isPreview, isOnboarding, itemType, scenarioId, id, scenarioTitle])

  const changeScenario = (changedScenario, validation) => {
    const changes = comparePayload(changedScenario, updatedScenario);

    // The blocker swallows the library's own post-init onChange, which
    // only emits `edited`-kind diffs on internal/normalized fields.
    // Structural user actions (added/deleted/moved nodes) must always go
    // through, otherwise the user's first edit after opening a scenario
    // gets silently dropped within the 2s window.
    if (changeBlockerRef.current) {
      const hasStructuralChange = changes.some(
        (c) => c.kind === 'added' || c.kind === 'deleted' || c.kind === 'moved'
      );
      if (!hasStructuralChange) {
        return;
      }
    }

    const changesWithoutIgnoredFields = changes.filter((change) => !IGNORED_TRAIL_FIELDS.some((field) => (change.fields ?? []).includes(field)));

    if (changesWithoutIgnoredFields.length > 0) {
      update?.(changedScenario, validation);
      setUpdatedScenario(changedScenario);
    }
  }

  const handleChatbotScenarioUpdate = (modifiedScenario, validation) => {
    console.log('handleChatbotScenarioUpdate called with scenario:', modifiedScenario);

    // Block normal change detection temporarily
    changeBlockerRef.current = true;

    // Update the scenario state
    console.log('>>>>>> 3')
    setUpdatedScenario(modifiedScenario);

    // Trigger the effect to handle remounting after state update
    setPendingScenarioUpdate({ modifiedScenario, validation });

    // Unblock after a delay
    setTimeout(() => {
      changeBlockerRef.current = false;
    }, 2000);
  }

  if ((!nodesConfig || !nodesDefinitions) && itemType !== 'surveyTask') {
    return null;
  }

  // Show error UI if editor error occurred
  if (editorError) {
    return (
      <>
        <div className="scenario-editor-error">
          Unable to render the scenario editor. Please try reloading the page or contact support if the issue persists.
        </div>

        {/* AI Assistant FAB still accessible */}
        {!isPreview && !chatbotOpen && (
          <ScenarioAiButton
            isOpen={chatbotOpen}
            onClick={() => setChatbotOpen(true)}
          />
        )}

        {chatbotOpen && (
          user?.id ? (
            <ScenarioChatbot
              key={`chatbot-${user.id}-${scenarioId}`}
              currentScenario={updatedScenario}
              tasks={onlyActiveTasks}
              userId={user.id}
              scenarioId={scenarioId}
              versionId={id}
              nodesConfig={nodesConfig}
              nodesDefinitions={nodesDefinitions}
              onScenarioUpdate={handleChatbotScenarioUpdate}
              onClose={() => setChatbotOpen(false)}
              fetchers={chatbotFetchers}
            />
          ) : (
            <div
              style={{
                position: 'fixed',
                right: '24px',
                bottom: '24px',
                width: '400px',
                padding: '20px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                zIndex: 2001,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, color: '#c62828' }}>Cannot Open Assistant</h3>
                <button
                  onClick={() => setChatbotOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#999',
                  }}
                >
                  ×
                </button>
              </div>
              <p style={{ margin: 0, color: '#666' }}>
                User authentication is required to use the Scenario Assistant. Please ensure you are logged in and try again.
              </p>
            </div>
          )
        )}
      </>
    );
  }

  // Add defensive checks to prevent errors
  const safeNodesConfig = itemsConfigOverride || nodesConfig;
  const safeNodesDefinitions = nodesDefinitions;

  // Debug logging
  console.log('Editor props:', {
    nodesConfig,
    nodesDefinitions,
    safeNodesConfig,
    safeNodesDefinitions,
    itemsConfigOverride,
    hasScenario: !!updatedScenario
  });

  // Early return if required props are missing
  // Note: Survey tasks don't require nodesDefinitions
  if ((!safeNodesConfig || !safeNodesDefinitions) && itemType !== 'surveyTask') {
    console.error('Missing required props:', { safeNodesConfig, safeNodesDefinitions });
    return (
      <div className="scenario-editor-error">
        Unable to load scenario editor configuration. Please try reloading the page.
      </div>
    );
  }

  console.log('>>>> updatedScenario', updatedScenario);

  return (
    <>
      <div
        id="scenario-editor-wrapper"
        key={editorKey}
        data-testid="scenario-editor"
        data-ready={changesAccepted ? "true" : "false"}
      >
        <ScenarioEditorErrorBoundary
          resetKey={editorKey}
          onError={(error) => setEditorError(error)}
          fallback={
            <div className="scenario-editor-error">
              Unable to render the scenario editor. Please try reloading the page or contact support if the issue persists.
            </div>
          }
        >
          <Editor
            key={editorKey}
            id={id}
            nodesConfig={safeNodesConfig}
            nodesDefinition={safeNodesDefinitions}
            // INFO: use here real scenario data (if undefined - empty scenario is used)
            defaultValue={updatedScenario ?? undefined}
            onChange={changeScenario}
            isPreview={isPreview}
            fetchers={editorFetchers}
          />
        </ScenarioEditorErrorBoundary>
      </div>

      {picker && (
        <ContentSearchModal
          open
          types={[picker.typeKey]}
          attachedIds={picker.attachedIds}
          context={`Scenario · ${scenarioTitle || 'Untitled'}`}
          onAdd={(items) =>
            closePicker(items.map((item) => ({ id: item.id, text: CONTENT_TYPES[picker.typeKey].titleOf(item) })))
          }
          onClose={() => closePicker(null)}
        />
      )}

      {/* AI Assistant FAB - Outside wrapper so it doesn't remount */}
      {!isPreview && !chatbotOpen && (
        <ScenarioAiButton
          isOpen={chatbotOpen}
          onClick={() => setChatbotOpen(true)}
        />
      )}

      {/* AI Chatbot - Outside wrapper so it doesn't remount */}
      {chatbotOpen && (
        user?.id ? (
          <ScenarioChatbot
              key={`chatbot-${user.id}-${scenarioId}`}
              currentScenario={updatedScenario}
              tasks={onlyActiveTasks}
              userId={user.id}
              scenarioId={scenarioId}
              versionId={id}
              nodesConfig={nodesConfig}
              nodesDefinitions={nodesDefinitions}
              onScenarioUpdate={handleChatbotScenarioUpdate}
              onClose={() => setChatbotOpen(false)}
              fetchers={chatbotFetchers}
            />
        ) : (
          <div
            style={{
              position: 'fixed',
              right: '24px',
              bottom: '24px',
              width: '400px',
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              zIndex: 2001,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#c62828' }}>Cannot Open Assistant</h3>
              <button
                onClick={() => setChatbotOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                }}
              >
                ×
              </button>
            </div>
            <p style={{ margin: 0, color: '#666' }}>
              User authentication is required to use the Scenario Assistant. Please ensure you are logged in and try again.
            </p>
          </div>
        )
      )}
    </>
  );
};

export default ScenarioEditorWrapper;
